import React, { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { loginAPI } from "../actioncreators/actioncreators";
import "../../App.css";
import brandConfig from "../../brandConfig";
import { useDispatch } from "react-redux";
import LoginPng from "../Assets/login.png";
import { emailId, registerePayload } from "../actions/loanAction";
import CountryDropdown from "../fields/CountryDropdown";
import StateDropdown from "../fields/StateDropdown";
import CityDropdown from "../fields/CityDropdown";
import PincodeDropdown from "../fields/PincodeDropdown";

const organizationSizes = ["1-10", "11-50", "51-200", "201-1000", "1000+"];
const organizationTypes = ["Govt. Office", "IT", "Finance", "Healthcare", "Education", "Manufacturing", "Retail", "Other"];

// Update the scrollbarHiddenStyle
const scrollbarHiddenStyle = {
  maxHeight: 'calc(100vh - 250px)', // Adjust based on header height and margins
  overflowY: 'auto',
  paddingRight: '10px'
};

// Add this style near the top of the file with other constants
const selectMenuStyle = {
  height: 'auto',
  maxHeight: '200px'
};

const OrganizationRegistration = () => {
  const [formData, setFormData] = useState({
    email: "",
    fullName: "",
    orgName: "",
    country: "India",
    countryCode: "IN",
    state: "",
    city: "",
    pincode: "",
    phoneCode: "+91",
    GSTNumber: "",
    phoneNumber: "",
    orgSize: "",
    orgType: "",
    // Opt-in: an organization only gets BLE (beacon) attendance if it asks for it here.
    beaconTrackingEnabled: false,
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [toaster, setToaster] = useState({ show: false, message: "", isError: false });
  const [gstError, setGstError] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Validate GST Number in real-time as user types
    if (name === "GSTNumber") {
      if (value) {
        const gstValidation = validateGSTNumber(value);
        setGstError(gstValidation.isValid ? "" : gstValidation.message);
      } else {
        setGstError("");
      }
    }
  };

  // GST Number validation function
  const validateGSTNumber = (gst) => {
    // GST format: 2 digits, 10 chars, 1 digit, 1 char, 1 digit, 1 char (total 15 chars)
    // Example: 27AAPFU0939F1ZV
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
    
    if (!gst) {
      return { isValid: true, message: "" };
    }
    if (!gstRegex.test(gst)) {
      return { isValid: false, message: "Invalid GST Number format" };
    }
    return { isValid: true, message: "" };
  };

  const handleCountryChange = (selectedCountry) => {
    setFormData({ 
      ...formData, 
      country: selectedCountry.name,
      countryCode: selectedCountry.code,
      state: "", // Reset state when country changes
      city: "", // Reset city when country changes
      phoneCode: selectedCountry.phoneCode 
    });
  };

  const handleStateChange = (e) => {
    setFormData({ 
      ...formData, 
      state: e.target.value,
      city: "" // Reset city when state changes
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate GST Number
    const gstValidation = validateGSTNumber(formData.GSTNumber);
    if (!gstValidation.isValid) {
      setGstError(gstValidation.message);
      return;
    }

    const payload = {
      email: formData.email,
      fullName: formData.fullName,
      orgName: formData.orgName,
      addressLine: formData.orgAddress,
      mobile: `${formData.phoneCode}${formData.phoneNumber}`,
      gstNumber: formData.GSTNumber,
      country: formData.country,
      state: formData.state,
      city: formData.city,
      pincode: formData.pincode,
      orgSize: formData.orgSize,
      orgType: formData.orgType,
      beaconTrackingEnabled: Boolean(formData.beaconTrackingEnabled),
    };
    dispatch(registerePayload(payload));
    try {
      const response = await loginAPI(payload);
      if (response.httpResponseCode === 200 && response.businessStatusCode === 2) {
        dispatch(emailId(response.emailId));
        toast.success(response.message || "OTP has been sent");
        navigate("/registerdOtp");
      } else {
        throw new Error(response.message || "Registration failed");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Registration failed";
      toast.error(errorMessage);
      setToaster({ show: true, message: errorMessage, isError: true });
      setTimeout(() => setToaster({ show: false, message: "", isError: false }), 3000);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="shadow-sm" style={{ backgroundColor: 'var(--brand-primary)' }}>
        <div className="px-3 py-2">
          <img src={brandConfig.logoPath} alt={brandConfig.appName} className="h-10" data-brand-logo="true" />
        </div>
      </header>

      <main className="flex-1 p-3 overflow-hidden">
        <div className="container mx-auto h-full">
          <div className="flex flex-wrap justify-center gap-4 h-full">
            {/* Left Column */}
            <div className="w-full md:w-6/12 bg-white rounded-lg p-4  text-center shadow">
              <img
                src={LoginPng}
                alt="ArthaTRACK"
                className="w-3/5 h-auto mx-auto object-contain"
              />
              <p className="text-center text-l mt-4">
                Streamline attendance, payroll, and compliance by easily tracking when your team is working.
              </p>
            </div>

            {/* Right Column */}
            <div className="w-full md:w-5/12 bg-white rounded-lg shadow p-4">
              <h1 className="text-xl font-bold mb-2">Create a new account</h1>
              <p className="text-gray-600  mb-4">Let's start your organization on the right track</p>

              <form 
                onSubmit={handleSubmit} 
                className="space-y-4 overflow-auto"
                style={scrollbarHiddenStyle}
              >
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-1000 focus:ring focus:ring-blue-200 focus:ring-opacity-50 h-9 text-xs"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                    Full Name 
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-1000 focus:ring focus:ring-blue-200 focus:ring-opacity-50 h-9 text-xs"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="orgName" className="block text-sm font-medium text-gray-700">
                    Organization Name
                  </label>
                  <input
                    type="text"
                    id="orgName"
                    name="orgName"
                    value={formData.orgName}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-1000 focus:ring focus:ring-blue-200 focus:ring-opacity-50 h-9 text-xs"
                    required
                  />
                </div>

                 <div>
                  <label htmlFor="orgAddress" className="block text-sm font-medium text-gray-700">
                   Address Line 1 
                  </label>
                  <input
                    type="text"
                    placeholder="Enter street address"
                    id="orgAddress"
                    name="orgAddress"
                    value={formData.orgAddress}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-1000 focus:ring focus:ring-blue-200 focus:ring-opacity-50 h-9 text-xs"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700">
                    Country
                  </label>
                  <CountryDropdown
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleCountryChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-1000 focus:ring focus:ring-blue-200 focus:ring-opacity-50 text-xs"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700">
                    State/Province
                  </label>
                  <StateDropdown
                    id="state"
                    name="state"
                    countryCode={formData.countryCode}
                    value={formData.state}
                    onChange={handleStateChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-1000 focus:ring focus:ring-blue-200 focus:ring-opacity-50 text-xs"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                    City
                  </label>
                  <CityDropdown
                    id="city"
                    name="city"
                    countryName={formData.country}
                    stateName={formData.state}
                    value={formData.city}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-1000 focus:ring focus:ring-blue-200 focus:ring-opacity-50 text-xs"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="pincode" className="block text-sm font-medium text-gray-700">
                    Pincode/Zipcode
                  </label>
                  <PincodeDropdown
                    id="pincode"
                    name="pincode"
                    countryName={formData.country}
                    stateName={formData.state}
                    cityName={formData.city}
                    value={formData.pincode}
                    onChange={handleInputChange}
                    placeholder="Enter pincode"
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-1000 focus:ring focus:ring-blue-200 focus:ring-opacity-50 h-9 text-xs px-3"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700">
                    Phone number
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-1000 text-sm">
                      {formData.phoneCode}
                    </span>
                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      className="flex-1 block w-full rounded-none rounded-r-md border border-gray-300 focus:border-blue-1000 focus:ring focus:ring-blue-200 focus:ring-opacity-50 h-9 text-xs"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="orgSize" className="block text-sm font-medium text-gray-700">
                    Organization Size
                  </label>
                  <select
                    id="orgSize"
                    name="orgSize"
                    value={formData.orgSize}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-1000 focus:ring focus:ring-blue-200 focus:ring-opacity-50 h-9 text-xs [&>option]:py-1"
                    style={selectMenuStyle}
                    required
                  >
                    <option value="">Select size</option>
                    {organizationSizes.map((size) => (
                      <option key={size} value={size} className="py-2">
                        {size}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="orgType" className="block text-sm font-medium text-gray-700">
                    Organization Type
                  </label>
                  <select
                    id="orgType"
                    name="orgType"
                    value={formData.orgType}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-1000 focus:ring focus:ring-blue-200 focus:ring-opacity-50 h-9 text-xs [&>option]:py-1"
                    style={selectMenuStyle}
                    required
                  >
                    <option value="">Select type</option>
                    {organizationTypes.map((type) => (
                      <option key={type} value={type} className="py-2">
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="GSTNumber" className="block text-sm font-medium text-gray-700">
                    GST Number (optional)
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      id="GSTNumber"
                      name="GSTNumber"
                      value={formData.GSTNumber}
                      onChange={handleInputChange}
                      className={`block w-full rounded-md border ${gstError ? 'border-red-1000' : 'border-gray-300'} shadow-sm focus:border-blue-1000 focus:ring focus:ring-blue-200 focus:ring-opacity-50 h-9 text-xs`}
                    />
                  </div>
                  {gstError && <p className="text-red-1000 text-xs mt-1">{gstError}</p>}
                </div>

                {/* BLE (beacon) attendance opt-in. Ticking it gives the organization
                    the Beacons menu, the User Presence tab, student alerts and all
                    BLE permissions automatically. */}
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="beaconTrackingEnabled"
                    name="beaconTrackingEnabled"
                    checked={formData.beaconTrackingEnabled}
                    onChange={(e) =>
                      setFormData({ ...formData, beaconTrackingEnabled: e.target.checked })
                    }
                    className="mt-1 h-4 w-4 cursor-pointer"
                  />
                  <label htmlFor="beaconTrackingEnabled" className="cursor-pointer text-sm font-medium text-gray-700">
                    Enable BLE (Beacon) Attendance
                    <span className="block text-xs font-normal text-gray-500 mt-0.5">
                      Track classroom or location attendance using Bluetooth beacons. Turns on the
                      Beacons menu, User Presence and student alerts for your organization.
                      You can change this later.
                    </span>
                  </label>
                </div>

                <button
                  type="submit"
                  className=" w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white" style={{ backgroundColor: 'var(--brand-accent)' }}
                >
                  Register
                </button>
                <p className="mt-4 text-center text-sm text-gray-600">
                  Already have an account?{" "}
                  <a href="/" className="font-medium text-primary">
                    Login
                  </a>
                </p>
              </form>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OrganizationRegistration;